import { useMutation, useQuery } from '@tanstack/react-query';
import { atom, useAtomValue, useSetAtom } from 'jotai';
import { useCallback, useEffect, useMemo } from 'react';
import { useAppContext } from 'src/contexts/AppContext';
import { ErrorTypes, useClientLogContext } from 'src/contexts/ClientLogContext';
import {
  EventDiscoveryClient,
  EventDiscoveryQuotaConfig,
} from 'src/WebApiController';

// atom flag to control whether or not we should try accessing the on sale event, which will consume the usage quota.
// We want to only enable this when we are in the on sale event page/section.
const shouldTryAccessOnSaleEventAtom = atom(false);

/**
 * This hooks is actually setting the atom flag to whether or not we should try accessing the on sale event.
 *
 * This needs to be controlled as try accessing the on sale event will consume the usage quota,
 * so we only want to do it when we are in the on sale event page.
 *
 * The atom is used to control when the query in useEventDiscoveryQuota should be enabled or not.
 */
export const useTryAccessOnSaleEvent = () => {
  const setTryAccessOnSaleEvent = useSetAtom(shouldTryAccessOnSaleEventAtom);
  useEffect(() => {
    setTryAccessOnSaleEvent(true);
    return () => {
      setTryAccessOnSaleEvent(false);
    };
  }, [setTryAccessOnSaleEvent]);
};

export const useEventDiscoveryQuota = (
  viagogoEventId: number | null,
  shouldRequestOnSaleAccessByDefault?: boolean
) => {
  const { activeAccountWebClientConfig } = useAppContext();
  const shouldTryAccessOnSaleEvent = useAtomValue(
    shouldTryAccessOnSaleEventAtom
  );

  const { trackError } = useClientLogContext();

  const shouldQuery = useMemo(() => {
    const isDataReady =
      Boolean(activeAccountWebClientConfig.activeAccountId) &&
      Boolean(viagogoEventId);
    if (shouldRequestOnSaleAccessByDefault) {
      return isDataReady;
    }

    // If the hook is disabled (from the initial render), we still want to override a check.
    // As this will control whether or not the on sale event page/section will try to access the on sale event.
    if (shouldTryAccessOnSaleEvent) {
      return isDataReady;
    }

    return false;
  }, [
    activeAccountWebClientConfig.activeAccountId,
    shouldRequestOnSaleAccessByDefault,
    shouldTryAccessOnSaleEvent,
    viagogoEventId,
  ]);

  const tryAccessOnSaleEventQuery = useQuery({
    queryKey: [
      'EventDiscoveryClient.getOnSaleEventAccountAccess',
      viagogoEventId,
      shouldTryAccessOnSaleEvent,
      shouldRequestOnSaleAccessByDefault,
    ],
    queryFn: async () => {
      if (!shouldQuery || !viagogoEventId) {
        return null;
      }

      const eventDiscoveryQuota = await new EventDiscoveryClient(
        activeAccountWebClientConfig
      ).tryAccessOnSaleEvent(viagogoEventId);

      return eventDiscoveryQuota;
    },

    enabled: shouldQuery,
    refetchOnWindowFocus: true,
    refetchInterval: 1000 * 60 * 2, // 2 minutes
    meta: {
      onError: (error: ErrorTypes) => {
        trackError('EventDiscoveryClient.getEventDiscoveryQuota', error);
      },
    },
  });

  const unlocakDemandDataMutation = useMutation({
    mutationFn: async ({
      quotaConfig,
    }: {
      quotaConfig: EventDiscoveryQuotaConfig;
    }) => {
      return await new EventDiscoveryClient(
        activeAccountWebClientConfig
      ).unlockEventDemandData(quotaConfig);
    },
    onError: (err: ErrorTypes, { quotaConfig }) => {
      trackError('EventDiscoveryClient.unlockEventDemandData', err, {
        trackErrorData: { quotaConfig },
      });
    },
    onSuccess: (value) => {
      if (value) {
        tryAccessOnSaleEventQuery.refetch();
      }
    },
  });

  const unlockDemandData = useCallback(
    async (quotaConfig: EventDiscoveryQuotaConfig) => {
      await unlocakDemandDataMutation.mutateAsync({ quotaConfig });
    },
    [unlocakDemandDataMutation]
  );

  return {
    onSaleEventAccess: tryAccessOnSaleEventQuery.data,
    isLoadingAccess: tryAccessOnSaleEventQuery.isLoading,
    isLoading: tryAccessOnSaleEventQuery.isLoading,
    unlockDemandData,
  };
};
